import { useMemo, useState } from 'react';

import { ENV } from '@/lib/constants/environment';

import { type LocalNavigationLink } from './useLocalNavigationMenuSections';

export type LocalNavigationEntryItem = {
  action?: 'open-ti-support' | 'logout';
  fragment?: string;
  icon?: LocalNavigationLink['icon'];
  queryParams?: Record<string, string>;
  queryParamsHandling?: 'merge' | 'preserve' | '';
  targetEnvironment?: string;
  text?: string;
  title?: string;
  url?: string;
};

// ─── URL helpers ─────────────────────────────────────────────────────────────

export const normalizeTargetEnvironment = (targetEnvironment?: string): string =>
  (targetEnvironment || '').trim().toLowerCase();

export const isExternalUrl = (url?: string): boolean => /^https?:\/\//i.test(url || '') || /^www\./i.test(url || '');

export const isLegacyTarget = (targetEnvironment?: string): boolean =>
  normalizeTargetEnvironment(targetEnvironment) === 'legacy';

export const isReactTarget = (targetEnvironment?: string): boolean =>
  normalizeTargetEnvironment(targetEnvironment) === 'react';

export const isExternalTarget = (targetEnvironment?: string): boolean => {
  return !isReactTarget(targetEnvironment);
};

export const normalizeUrl = (url?: string): string => {
  if (!url) return '';
  const [path] = url.split(/[?#]/);
  if (!path) return '';
  if (path === '/') return path;
  return path.endsWith('/') ? path.slice(0, -1) : path;
};

const getTargetBaseUrl = (targetEnvironment?: string): string => {
  if (isLegacyTarget(targetEnvironment)) return ENV.LEGACY_PLATFORM_URL;
  return ENV.ANGULAR_PLATFORM_URL;
};

const joinUrl = (baseUrl: string, path: string): string => {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
};

export const buildItemSearch = (
  queryParams?: Record<string, string>,
  queryParamsHandling?: 'merge' | 'preserve' | ''
): URLSearchParams => {
  const currentSearch = typeof window === 'undefined' ? '' : window.location.search;
  const currentParams = new URLSearchParams(currentSearch);

  if (queryParamsHandling === 'preserve' && !queryParams) return currentParams;

  const nextParams =
    queryParamsHandling === 'merge' || queryParamsHandling === 'preserve'
      ? new URLSearchParams(currentParams)
      : new URLSearchParams();

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => nextParams.set(key, value));
  }

  return nextParams;
};

export const getRouterSearch = (
  item: Pick<LocalNavigationEntryItem, 'queryParams' | 'queryParamsHandling'>
): Record<string, string> | undefined => {
  const search = buildItemSearch(item.queryParams, item.queryParamsHandling);
  const entries = Object.fromEntries(search.entries());
  return Object.keys(entries).length ? entries : undefined;
};

const buildHref = (
  item: Pick<LocalNavigationEntryItem, 'url' | 'fragment' | 'queryParams' | 'queryParamsHandling'>
): string | null => {
  if (!item.url) return null;
  const search = buildItemSearch(item.queryParams, item.queryParamsHandling).toString();
  const hash = item.fragment ? `#${item.fragment}` : '';
  return `${item.url}${search ? `?${search}` : ''}${hash}`;
};

export const getExternalUrl = (
  item: Pick<LocalNavigationEntryItem, 'url' | 'targetEnvironment' | 'fragment' | 'queryParams' | 'queryParamsHandling'>
): string | null => {
  if (!item.url) return null;
  if (isExternalUrl(item.url)) return item.url;
  if (isReactTarget(item.targetEnvironment)) return null;
  const baseUrl = getTargetBaseUrl(item.targetEnvironment);
  if (!baseUrl) return null;
  const href = buildHref(item);
  if (!href) return null;
  return joinUrl(baseUrl, href);
};

export const getRouterLink = (item: { url?: string; targetEnvironment?: string }): string | null => {
  if (!item.url) return null;
  if (isExternalUrl(item.url) || !isReactTarget(item.targetEnvironment)) return null;
  return item.url;
};

export const isRouteActive = (
  pathname: string,
  item: { url?: string; targetEnvironment?: string },
  options?: { exact?: boolean }
): boolean => {
  const routerLink = getRouterLink(item);
  if (!routerLink) return false;
  const normalizedCurrent = normalizeUrl(pathname);
  const normalizedTarget = normalizeUrl(routerLink);
  if (!normalizedCurrent || !normalizedTarget) return false;
  if (options?.exact) return normalizedCurrent === normalizedTarget;
  return normalizedCurrent === normalizedTarget || normalizedCurrent.startsWith(`${normalizedTarget}/`);
};

export const getFilledIconName = (icon?: LocalNavigationLink['icon']): LocalNavigationLink['icon'] | undefined => {
  if (!icon) return undefined;
  if (typeof icon !== 'string') return icon;
  if (icon.endsWith('-fill')) return icon;
  return `${icon}-fill` as LocalNavigationLink['icon'];
};

export const getIconColor = (): string => 'foreground.brand';

// ─── Hook ─────────────────────────────────────────────────────────────────────

type UseLocalNavigationEntryReturn = {
  isHovered: boolean;
  isHighlighted: boolean;
  routerLink: string | null;
  routerSearch: Record<string, string> | undefined;
  href: string | null;
  sharedProps: {
    'aria-label': string;
    title: string;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onFocus: () => void;
    onBlur: () => void;
  };
};

export function useLocalNavigationEntry(
  item: LocalNavigationEntryItem,
  active: boolean
): UseLocalNavigationEntryReturn {
  const [isHovered, setIsHovered] = useState(false);
  const label = item.text || item.title || '';

  const routerLink = getRouterLink(item);
  const href = getExternalUrl(item);
  const routerSearch = useMemo(
    () => getRouterSearch({ queryParams: item.queryParams, queryParamsHandling: item.queryParamsHandling }),
    [item.queryParams, item.queryParamsHandling]
  );

  const sharedProps = {
    'aria-label': label,
    title: label,
    onMouseEnter: (): void => setIsHovered(true),
    onMouseLeave: (): void => setIsHovered(false),
    onFocus: (): void => setIsHovered(true),
    onBlur: (): void => setIsHovered(false),
  };

  return {
    isHovered,
    isHighlighted: active || isHovered,
    routerLink,
    routerSearch,
    href,
    sharedProps,
  };
}
